import React, {
    createContext, useEffect, useReducer, useMemo
} from 'react';

const initialState = {
    instances: {},
    latestItemId: null,
    itemToCloseId: null,
    parentBounding: null,
}

const actions = {
    SET_IS_VISIBLE: 'SET_IS_VISIBLE',
    CLOSE_MENU_INSTANCE: 'CLOSE_MENU_INSTANCE',
    REMOVE_INSTANCE: 'REMOVE_INSTANCE',
    CREATE_MENU_INSTANCE_BY_PARENT_POSITION: 'CREATE_MENU_INSTANCE_BY_PARENT_POSITION',
    SET_MENU_POSITION_AND_CREATE_NEW_MENU_INSTANCE: 'SET_MENU_POSITION_AND_CREATE_NEW_MENU_INSTANCE',
    SET_SELECTED_ITEM: 'SET_SELECTED_ITEM',
}

const reducer = (state, action) => {
    switch (action.type) {
        case actions.SET_IS_VISIBLE:
            return state.latestItemId ? {
                ...state,
                instances: {
                    ...state.instances,
                    [state.latestItemId]: {
                        ...state.instances[state.latestItemId],
                        isVisible: action.payload
                    }
                }
            } : state;
        case actions.CLOSE_MENU_INSTANCE:
            return {
                ...state,
                latestItemId: state.latestItemId === action.id ? null : state.latestItemId,
                itemToCloseId: action.id,
                instances: { ...state.instances, [action.id]: { ...state.instances[action.id], isVisible: false } }
            };
        case actions.REMOVE_INSTANCE:
            return {
                ...state,
                itemToCloseId: null,
                instances: Object.fromEntries(Object.entries(state.instances).filter(([key]) => key !== action.payload))
            }
        case actions.CREATE_MENU_INSTANCE_BY_PARENT_POSITION:
            return { ...state, parentBounding: action.parentBounding, latestItemId: action.id };
        case actions.SET_MENU_POSITION_AND_CREATE_NEW_MENU_INSTANCE:
            return state.latestItemId ? {
                ...state,
                instances: {
                    ...state.instances,
                    [state.latestItemId]: {
                        selectedItem: null,
                        menuTop: action.menuTop,
                        menuLeft: action.menuLeft,
                        menuBottom: action.menuBottom,
                        menuRight: action.menuRight,
                        isVisible: false
                    }
                }
            } : state;
        case actions.SET_SELECTED_ITEM:
            return state.latestItemId ? {
                ...state,
                instances: {
                    ...state.instances,
                    [state.latestItemId]: { ...state.instances[state.latestItemId], selectedItem: action.payload }
                }
            } : state;
        default:
            return state;
    }
}

export const PopupMenuContext = createContext();

const PopupMenuContextProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);


    useEffect(() => {
        state.parentBounding?.top && calculateAndSetMenuPosition(state?.parentBounding);

        const repositionMenuOnResize = () => {
            calculateAndSetMenuPosition(state.parentBounding);
        }

        window.addEventListener('resize', repositionMenuOnResize);
        return () => { window.removeEventListener('resize', repositionMenuOnResize); }
    }, [state.parentBounding]);

    const calculateAndSetMenuPosition = (parentBounding) => {
        const pageWidth = window.innerWidth;
        const pageHeight = window.innerHeight;

        // calcuate vertical position
        let top;
        let bottom;
        let left;
        let right;

        const triggerElementHalfHeight = (parentBounding?.bottom - parentBounding?.top) / 2;
        if (window.innerWidth <= 768) {
            bottom = 0;
        }
        else if (parentBounding?.top <= pageHeight / 2) {
            // if trigger element is in top half of the page
            parentBounding?.top && (top = parentBounding.top + triggerElementHalfHeight);
        }
        else {
            // if trigger element is in bottom half of the page
            parentBounding?.bottom && (bottom = pageHeight - parentBounding.bottom + triggerElementHalfHeight);
        }

        // calculate horizontal position
        const triggerElementHalfWidth = (parentBounding?.right - parentBounding?.left) / 2;
        const leftReservedArea = 250;
        if (pageWidth - parentBounding?.right <= leftReservedArea) {
            // // if element is in the reserved right half , render it towards left
            // parentBounding?.right && (right = pageWidth - parentBounding.right + (triggerElementHalfWidth * 1.5));

            // if element is in the reserved right half
            // ⚠️ 256 is the width of the pop up menu and needs to updated if changes for any reason.
            parentBounding?.right && (left = parentBounding.left - (triggerElementHalfWidth) - 256);

        } else {
            // if element is in the main left area , render it towards right
            parentBounding?.left && (left = parentBounding.left + (triggerElementHalfWidth * 1.5));
        }

        dispatch({
            type: actions.SET_MENU_POSITION_AND_CREATE_NEW_MENU_INSTANCE,
            menuTop: top,
            menuLeft: left,
            menuBottom: bottom,
            menuRight: right
        });
    }

    // (transition related)
    useEffect(() => {
        const makeVisibleTimeout = setTimeout(() => {
            if (state?.instances?.[state?.latestItemId] &&
                (state?.instances?.[state?.latestItemId]?.isVisible === false)) {
                dispatch({ type: actions.SET_IS_VISIBLE, payload: true });
            }
        }, 100);
        return () => { clearTimeout(makeVisibleTimeout) }
    }, [state.instances, state.latestItemId]);


    // (transition related) to close with transition
    useEffect(() => {
        const makeVisibleTimeout = setTimeout(() => {
            if (state.itemToCloseId) {
                dispatch({ type: actions.REMOVE_INSTANCE, payload: state.itemToCloseId });
            }
        }, 100);
        return () => { clearTimeout(makeVisibleTimeout); }
    }, [state.itemToCloseId]);


    const value = useMemo(() => ({
        closeMenuInstance: (id) => { dispatch({ type: actions.CLOSE_MENU_INSTANCE, id }) },
        parentBounding: state.parentBounding,
        openMenuByParentPosition: (parentBounding, id) => {
            dispatch({ type: actions.CREATE_MENU_INSTANCE_BY_PARENT_POSITION, parentBounding, id })
        },
        instances: state.instances,
        latestItemId: state.latestItemId,
        // ⚠️ note: wait 10 ms to prevent from conflict on menu closeclick event listener,
        // (if we use it without setTimeout, then mean while it's removing the content to set
        // second stage's content, it checks existence of the clicked item in the container
        // and as it's not there, it will closes popup unexpectedly.)
        setSelectedItem: payload => {
            setTimeout(() => {
                dispatch({ type: actions.SET_SELECTED_ITEM, payload })
            }, 10)
        },
    }), [state, dispatch]);

    return (
        <PopupMenuContext.Provider value={value}>
            {children}
        </PopupMenuContext.Provider>
    );
}

export default PopupMenuContextProvider;
